import React, { createContext, useState, useContext, useEffect, useRef } from "react";
import { useMsal } from "@azure/msal-react";
import { setupAxiosInterceptors, refreshTokenProactively } from "./services/AxiosInstance";
import { isExpired } from "react-jwt";

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
    const { instance } = useMsal();
    const [showLogoutDialog, setShowLogoutDialog] = useState(false);
    const lastActivityTime = useRef(Date.now());
    const lastActivityUpdate = useRef(Date.now());
    const tokenRefreshInterval = useRef(null);

    const handleLogout = () => {
        setShowLogoutDialog(true);
    };

    const confirmLogout = () => {
        localStorage.removeItem("central-site-token")
        localStorage.removeItem("central-site-user")
        localStorage.removeItem("central-site-delimitation")
        sessionStorage.clear()

        setShowLogoutDialog(false);
        instance.logoutRedirect();
    };

    // Track user activity (throttled to once per minute)
    const updateActivity = () => {
        const now = Date.now();
        // Only update activity timestamp once per minute to reduce overhead
        if (now - lastActivityUpdate.current > 60000) {
            lastActivityTime.current = now;
            lastActivityUpdate.current = now;
        }
    };

    useEffect(() => {
        setupAxiosInterceptors(handleLogout); // Setup interceptor with logout callback

        // Add event listeners to track user activity
        const activityEvents = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
        activityEvents.forEach(event => {
            document.addEventListener(event, updateActivity, true);
        });

        // Check token and refresh proactively every 15 minutes if user is active
        tokenRefreshInterval.current = setInterval(async () => {
            const token = localStorage.getItem("central-site-token");
            const isUserActive = (Date.now() - lastActivityTime.current) < 30 * 60 * 1000; // Active within last 30 minutes

            if (token && isUserActive) {
                try {
                    const tokenExpired = isExpired(token);
                    const decodedToken = JSON.parse(atob(token.split('.')[1]));
                    const expiryTime = decodedToken.exp * 1000; // Convert to milliseconds
                    const timeUntilExpiry = expiryTime - Date.now();
                    const fifteenMinutes = 15 * 60 * 1000;

                    // Refresh token if it's within 15 minutes of expiring or already expired
                    if (tokenExpired || timeUntilExpiry < fifteenMinutes) {
                        console.log("Proactively refreshing token due to user activity...");
                        await refreshTokenProactively(handleLogout);
                    }
                } catch (error) {
                    console.error("Error checking token expiration:", error);
                }
            }
        }, 15 * 60 * 1000); // Check every 15 minutes

        // Cleanup
        return () => {
            activityEvents.forEach(event => {
                document.removeEventListener(event, updateActivity, true);
            });
            if (tokenRefreshInterval.current) {
                clearInterval(tokenRefreshInterval.current);
            }
        };
    }, []);

    return (
        <AuthContext.Provider value={{ showLogoutDialog, handleLogout, confirmLogout }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => useContext(AuthContext);
